// Gmail-style formatting toolbar that sits above a contentEditable surface.
//
// Commands grouped into:
//   1. Inline:    Bold · Italic · Underline · Strikethrough · Inline code
//   2. Lists:     Bulleted · Numbered
//   3. Indent:    Increase indent · Decrease indent
//   4. Block:     Blockquote
//   5. Link:      Insert link
//   6. Reset:     Clear formatting
//
// Engine choice: `document.execCommand`. Marked deprecated in the spec, but
// every browser still ships it, and Gmail itself uses it for the same set of
// operations. The "modern" alternatives (Selection API + DOM mutation) are
// 10× the code for the same UX in an email composer that doesn't need
// collaborative editing or schema enforcement.
//
// Active-button state refreshes on `selectionchange` events filtered to the
// editor element. queryCommandState handles B/I/U/S/lists; we add custom
// ancestor walks for `<code>` and `<blockquote>` since execCommand has no
// query for those.
//
// `mousedown.preventDefault()` on every interactive surface keeps the
// editor's selection from collapsing when you mouse-click a button. Without
// that, the very first click loses the highlighted text and the command is
// a no-op.
//
// What's deliberately NOT here: colors, fonts, sizes, headings, alignment.
// Email clients render those wildly inconsistently and they look gimmicky in
// business correspondence. Bold / italic / lists / quotes / code / links are
// the safe set that works everywhere.

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Indent,
  Outdent,
  Quote,
  Link as LinkIcon,
  Eraser,
  MoreHorizontal,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { RichBodyEditorHandle } from '@/components/RichBodyEditor';
import { EmojiPickerButton } from '@/components/EmojiPicker';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface Props {
  /** Ref to the editor whose selection we operate on. */
  editorRef: React.RefObject<RichBodyEditorHandle | null>;
  className?: string;
  /** Optional callback fired after any toolbar command executes — gives the
   *  parent a chance to mirror the new HTML into its state (autosave etc.).
   *  In practice the editor's own `onChange` already fires from the input
   *  event triggered by execCommand, so this is mostly for slash-menu /
   *  link-insert paths that complete asynchronously. */
  onCommand?: () => void;
  /** Where the toolbar sits relative to the editor.
   *
   *  `top` (default): toolbar renders ABOVE the editor, divider is on
   *  the bottom edge (`border-b`), emoji popover opens DOWNWARD so it
   *  doesn't push out of the dialog top.
   *
   *  `bottom`: toolbar renders BELOW the editor — easier reach as a
   *  reply grows long (the user's cursor lives at the end of their
   *  text, which is bottom-of-editor, which is right above the toolbar).
   *  Divider flips to the top edge (`border-t`) and the emoji popover
   *  opens UPWARD so it overlays the editor text instead of disappearing
   *  off the bottom of the page. */
  position?: 'top' | 'bottom';
  /** Action-button slot — rendered to the right of the formatting +
   *  emoji glyphs, separated by a divider. Used by ReplyComposer +
   *  ComposeDialog to fold their Draft / Polish / Templates / Attach /
   *  Save-as-template / Send buttons INTO the floating sticky bar so
   *  every composer affordance is in one place (instead of split
   *  between a sticky formatting bar and a static action row below).
   *  Pass nothing for read-only formatting surfaces. */
  actionsAfter?: ReactNode;
}

interface ButtonState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  code: boolean;
  ul: boolean;
  ol: boolean;
  blockquote: boolean;
}

const EMPTY_STATE: ButtonState = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  code: false,
  ul: false,
  ol: false,
  blockquote: false,
};

export function RichBodyToolbar({ editorRef, className, onCommand, position = 'top', actionsAfter }: Props) {
  const [state, setState] = useState<ButtonState>(EMPTY_STATE);
  // Track the editor element so selectionchange events fire only when the
  // user is actually working inside our composer.
  const editorElRef = useRef<HTMLElement | null>(null);

  // ─── Sticky-stuck detection (bottom-positioned toolbar only) ────────────
  //
  // When the toolbar is sticky at viewport bottom, we want it to look like
  // a floating "island" (centred pill, shadow, pulled off the edge). When
  // the user scrolls to the very end of the composer body and the toolbar
  // parks at its natural slot, we want the docked appearance (full width,
  // flush border, no shadow). Pure CSS sticky doesn't expose a "currently
  // stuck" state, so we use the standard sentinel + IntersectionObserver
  // pattern:
  //   1. Render a 1px sentinel div RIGHT AFTER the toolbar in DOM order.
  //   2. Observe it. When the sentinel is in the viewport → toolbar is
  //      parked at its natural slot. When the sentinel is below the
  //      viewport (because the composer body extends past viewport bottom),
  //      the toolbar is sticking.
  //   3. `isStuck` drives the className swap; `transition-all` animates
  //      the change so the docked → island morph is smooth.
  //
  // Only relevant for `position='bottom'`. For `position='top'` the toolbar
  // is a normal block element and isStuck stays false.
  const [isStuck, setIsStuck] = useState(false);
  // Controlled open for the emoji picker, so the keyboard shortcut below can
  // pop it open (the button click still toggles it the normal way).
  const [emojiOpen, setEmojiOpen] = useState(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (position !== 'bottom') return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        // Sentinel visible → toolbar parked. Sentinel offscreen → toolbar
        // sticking (or scrolled past entirely, but that case the toolbar
        // is invisible anyway so the styling doesn't matter).
        setIsStuck(!entry.isIntersecting);
      },
      { threshold: 0 },
    );
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, [position]);

  useEffect(() => {
    editorElRef.current = editorRef.current?.getElement() ?? null;
  });

  // Keyboard shortcut to open the emoji picker: ⌘/Ctrl + Shift + E. Scoped to
  // THIS editor (only fires when the caret is inside it) so multiple composers
  // on one page don't all react. Matches the ⌘⇧D / ⌘⇧P composer-action style.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey) return;
      if (e.key !== 'E' && e.key !== 'e') return;
      const el = editorElRef.current;
      const active = document.activeElement;
      if (!el || !active || !el.contains(active)) return;
      e.preventDefault();
      setEmojiOpen(true);
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Refresh active-button state whenever the selection changes inside the
  // editor. queryCommandState reflects the current caret position's
  // formatting context.
  useEffect(() => {
    function refresh() {
      const el = editorElRef.current;
      const sel = window.getSelection();
      if (!el || !sel || sel.rangeCount === 0) {
        setState(EMPTY_STATE);
        return;
      }
      const anchor = sel.anchorNode;
      if (!anchor || !el.contains(anchor)) return;
      try {
        const blockType = (() => {
          try {
            return String(document.queryCommandValue('formatBlock') || '').toLowerCase();
          } catch {
            return '';
          }
        })();
        setState({
          bold: document.queryCommandState('bold'),
          italic: document.queryCommandState('italic'),
          underline: document.queryCommandState('underline'),
          strike: document.queryCommandState('strikeThrough'),
          code: hasAncestor(anchor, el, 'CODE'),
          ul: document.queryCommandState('insertUnorderedList'),
          ol: document.queryCommandState('insertOrderedList'),
          blockquote: blockType === 'blockquote' || hasAncestor(anchor, el, 'BLOCKQUOTE'),
        });
      } catch {
        // queryCommandState can throw on some Firefox versions if the editor
        // has been removed from the DOM mid-event. Safe to swallow — next
        // selectionchange will rebuild state.
      }
    }
    document.addEventListener('selectionchange', refresh);
    return () => document.removeEventListener('selectionchange', refresh);
  }, []);

  // Run an execCommand against the editor. Re-focuses first so a mouse-click
  // on a button doesn't drop the caret position.
  const runCommand = useCallback(
    (command: string, value?: string) => {
      const el = editorElRef.current;
      if (!el) return;
      el.focus();
      try {
        document.execCommand(command, false, value);
      } catch {
        // Same Firefox edge case as the query path.
      }
      onCommand?.();
    },
    [onCommand],
  );

  // The mobile "More" formatter menu is a Radix dropdown — opening it pulls
  // focus into the menu, which deactivates the editor's text selection. We
  // snapshot the selection when the menu opens and restore it before running
  // the command, so e.g. "Underline" still applies to the text that WAS
  // selected (not a collapsed caret).
  const savedRangeRef = useRef<Range | null>(null);
  function saveSelection() {
    const el = editorElRef.current;
    const sel = window.getSelection();
    if (el && sel && sel.rangeCount > 0) {
      const r = sel.getRangeAt(0);
      if (el.contains(r.commonAncestorContainer)) savedRangeRef.current = r.cloneRange();
    }
  }
  function withSavedSelection(fn: () => void) {
    const el = editorElRef.current;
    const r = savedRangeRef.current;
    if (el && r) {
      el.focus();
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(r);
    }
    fn();
  }

  // Inline code toggle. execCommand has no `code` operation, so we wrap the
  // selection in `<code>` tags via insertHTML when off, and unwrap by
  // replacing the surrounding code element's outerHTML with its innerHTML
  // when on.
  const toggleInlineCode = useCallback(() => {
    const el = editorElRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const codeAncestor = findAncestor(range.startContainer, el, 'CODE');
    if (codeAncestor) {
      // Already inside a <code>: unwrap by replacing it with its text.
      const text = codeAncestor.textContent || '';
      const textNode = document.createTextNode(text);
      codeAncestor.replaceWith(textNode);
      // Re-select the unwrapped text.
      const newRange = document.createRange();
      newRange.selectNodeContents(textNode);
      sel.removeAllRanges();
      sel.addRange(newRange);
    } else {
      // Wrap selection in <code>. If selection is collapsed, insert an
      // empty <code> with caret inside.
      const selected = sel.toString();
      if (selected.length > 0) {
        document.execCommand(
          'insertHTML',
          false,
          `<code>${escapeHtml(selected)}</code>`,
        );
      } else {
        document.execCommand('insertHTML', false, '<code>​</code>');
      }
    }
    onCommand?.();
  }, [onCommand]);

  // Blockquote toggle. formatBlock with 'blockquote' wraps the current
  // paragraph; toggling it back uses 'div' (browser default for blocks).
  const toggleBlockquote = useCallback(() => {
    const el = editorElRef.current;
    if (!el) return;
    el.focus();
    let isQuote = false;
    try {
      isQuote = String(document.queryCommandValue('formatBlock') || '').toLowerCase() === 'blockquote';
    } catch {
      // ignore
    }
    runCommand('formatBlock', isQuote ? 'div' : 'blockquote');
  }, [runCommand]);

  // Insert link prompts for a URL. We deliberately use prompt() instead of a
  // custom modal — keeps the toolbar zero-dep and matches Gmail's behavior
  // for the basic "make this text a link" path.
  const insertLink = useCallback(() => {
    const sel = window.getSelection();
    const hasSelection = sel && sel.toString().length > 0;
    const url = window.prompt(
      hasSelection ? 'Link URL:' : 'Link URL (text will use the URL itself):',
      'https://',
    );
    if (!url) return;
    const trimmed = url.trim();
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('mailto:')) {
      window.alert('Link must start with http://, https://, or mailto:');
      return;
    }
    if (hasSelection) {
      runCommand('createLink', trimmed);
    } else {
      runCommand(
        'insertHTML',
        `<a href="${escapeHtml(trimmed)}" target="_blank" rel="noopener noreferrer">${escapeHtml(trimmed)}</a>`,
      );
    }
  }, [runCommand]);

  // Strip inline formatting from the current selection. removeFormat handles
  // B/I/U/S/colors/fonts; we chase it with explicit toggles for lists and
  // blockquote since those aren't covered.
  const clearFormatting = useCallback(() => {
    const el = editorElRef.current;
    if (!el) return;
    el.focus();
    try {
      document.execCommand('removeFormat');
      if (document.queryCommandState('insertUnorderedList')) {
        document.execCommand('insertUnorderedList');
      }
      if (document.queryCommandState('insertOrderedList')) {
        document.execCommand('insertOrderedList');
      }
      const blockType = String(document.queryCommandValue('formatBlock') || '').toLowerCase();
      if (blockType === 'blockquote') {
        document.execCommand('formatBlock', false, 'div');
      }
    } catch {
      // ignore
    }
    onCommand?.();
  }, [onCommand]);

  // Less-common formatters. Inline on >=sm; collapsed into a ⋯ "More" menu on
  // mobile. Defined once and rendered in both places so behaviour can't drift.
  const moreItems: {
    key: string;
    label: string;
    title: string;
    icon: ReactNode;
    active?: boolean;
    onClick: () => void;
  }[] = [
    { key: 'strike', label: 'Strikethrough', title: 'Strikethrough', icon: <Strikethrough className="h-3.5 w-3.5" />, active: state.strike, onClick: () => runCommand('strikeThrough') },
    { key: 'code', label: 'Inline code', title: 'Inline code', icon: <Code className="h-3.5 w-3.5" />, active: state.code, onClick: toggleInlineCode },
    { key: 'ul', label: 'Bulleted list', title: 'Bulleted list', icon: <List className="h-3.5 w-3.5" />, active: state.ul, onClick: () => runCommand('insertUnorderedList') },
    { key: 'ol', label: 'Numbered list', title: 'Numbered list', icon: <ListOrdered className="h-3.5 w-3.5" />, active: state.ol, onClick: () => runCommand('insertOrderedList') },
    { key: 'outdent', label: 'Decrease indent', title: 'Decrease indent', icon: <Outdent className="h-3.5 w-3.5" />, onClick: () => runCommand('outdent') },
    { key: 'indent', label: 'Increase indent', title: 'Increase indent', icon: <Indent className="h-3.5 w-3.5" />, onClick: () => runCommand('indent') },
    { key: 'quote', label: 'Quote', title: 'Quote', icon: <Quote className="h-3.5 w-3.5" />, active: state.blockquote, onClick: toggleBlockquote },
    { key: 'clear', label: 'Clear formatting', title: 'Clear formatting', icon: <Eraser className="h-3.5 w-3.5" />, onClick: clearFormatting },
  ];

  return (
    <>
    <div
      // `role="toolbar"` tells assistive tech to expect arrow-key roving
      // between the controls. That was never implemented, so the role promised
      // a navigation model that doesn't exist — worse than omitting it.
      // `group` keeps the labelled grouping without the false promise.
      role="group"
      aria-label="Formatting"
      className={cn(
        'flex items-center gap-0.5 px-1.5 py-1',
        // Smooth island ↔ docked morph. transition-all animates width
        // (max-w-fit), border-radius, shadow, margin all at once so the
        // shape change reads as a single fluid motion. duration-200
        // matches the rest of the app's micro-interactions (toasts,
        // dialog enter/exit).
        position === 'bottom' && 'transition-all duration-200',
        // Divider sits on whichever edge faces the editor — bottom edge
        // when the toolbar is above (default), top edge when it's below.
        position === 'top'
          ? 'border-b border-border/60 bg-muted/30'
          : // Bottom-positioned toolbar STICKS to the viewport bottom
            // while any part of the editor is on screen. Without this,
            // a long reply forces the agent to scroll to the very end
            // of the typed text just to reach the Bold button — the
            // toolbar lives there but is below the fold. Sticky keeps
            // it docked at viewport bottom so the formatting glyphs +
            // emoji are always one click away no matter where in the
            // reply the cursor is.
            //
            // Two appearances driven by `isStuck`:
            //   - STUCK (floating mid-editor): centred pill, rounded
            //     all the way, soft shadow + ring, pulled up off the
            //     viewport edge by mb-3. Reads as a floating "island"
            //     hovering over the editor content.
            //   - PARKED (scrolled to bottom of composer): full width,
            //     top divider border, no shadow, flush with the
            //     editor. Reads as the normal docked toolbar.
            // The transition between the two animates smoothly thanks
            // to `transition-all duration-200` above.
            // `bottom-6` (NOT bottom-0) parks the floating island 1.5rem
            // above the viewport edge so it doesn't feel glued to the
            // bottom of the screen. `mb-X` won't do this — margin sits
            // outside the sticky box, so the element still anchors at
            // `bottom`. The 1.5rem gap also gives breathing room above
            // mobile gesture bars / iOS home indicator. When parked at
            // the bottom of the composer (docked), the bottom offset
            // is invisible because the toolbar's natural slot is what
            // determines its position.
            'sticky bottom-6 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85',
        position === 'bottom' &&
          (isStuck
            // Desktop: centred floating pill. Mobile: a full-width rounded
            // bar (NOT a pill) — the pill looks cramped/ugly once the buttons
            // need to wrap, so on small screens we go edge-to-edge and let the
            // formatting cluster scroll horizontally instead.
            ? 'mx-auto max-w-fit rounded-full border border-border/80 shadow-lg ring-1 ring-black/5 max-sm:mx-0 max-sm:max-w-none max-sm:rounded-xl'
            : 'mx-0 max-w-none rounded-none border-t border-border/60 shadow-none ring-0'),
        className,
      )}
      onMouseDown={(e) => e.preventDefault()}
    >
      {/* Formatting cluster. The comment on the wrapper above claims small
          screens "let the formatting cluster scroll horizontally instead" —
          but no scroll container existed, and the root is `flex` (nowrap by
          default). At 375px the row needs roughly 448px, so the glyphs simply
          squashed and the tail was clipped by `overflow-x-clip` on the app
          shell. This is the container that comment described.

          Send and the other composer actions live OUTSIDE it, so the primary
          action can never be scrolled out of reach. */}
      <div className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <ToolbarButton active={state.bold} title="Bold (⌘B)" onClick={() => runCommand('bold')} aria-label="Bold">
        <Bold className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton active={state.italic} title="Italic (⌘I)" onClick={() => runCommand('italic')} aria-label="Italic">
        <Italic className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton active={state.underline} title="Underline (⌘U)" onClick={() => runCommand('underline')} aria-label="Underline">
        <Underline className="h-3.5 w-3.5" />
      </ToolbarButton>
      <ToolbarButton title="Insert link" onClick={insertLink} aria-label="Insert link">
        <LinkIcon className="h-3.5 w-3.5" />
      </ToolbarButton>

      <EmojiPickerButton
        size="sm"
        side={position === 'top' ? 'bottom' : 'top'}
        align="right"
        open={emojiOpen}
        onOpenChange={setEmojiOpen}
        title="Insert an emoji (⌘⇧E)"
        onPick={(emoji) => {
          // Reuse the toolbar's focus + execCommand plumbing so the caret
          // stays put and the editor's input event fires (→ parent autosave).
          runCommand('insertText', emoji);
        }}
      />

      {/* Secondary formatters — inline on >=sm. */}
      <div className="hidden items-center gap-0.5 sm:flex">
        <Separator />
        {moreItems.map((item) => (
          <ToolbarButton key={item.key} active={item.active} title={item.title} onClick={item.onClick} aria-label={item.label}>
            {item.icon}
          </ToolbarButton>
        ))}
      </div>

      {/* Secondary formatters — ⋯ "More" menu on mobile. saveSelection on open
          + withSavedSelection per command keeps the editor's text selection so
          e.g. Underline still applies to what was highlighted. */}
      <div className="sm:hidden">
        <DropdownMenu onOpenChange={(o) => { if (o) saveSelection(); }}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title="More formatting"
              aria-label="More formatting"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side={position === 'top' ? 'bottom' : 'top'}
            className="w-44"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            {moreItems.map((item) => (
              <DropdownMenuItem
                key={item.key}
                onSelect={() => withSavedSelection(item.onClick)}
                className={cn('gap-2 text-sm', item.active && 'bg-accent/60 font-medium')}
              >
                {item.icon} {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      </div>
      {/* Composer-level actions (Draft, Polish, Templates, Attach,
          Save-as-template, Send) — supplied by the parent via the
          `actionsAfter` slot so they share the same floating bar as the
          formatting glyphs. `shrink-0` and placement outside the scrolling
          formatting cluster guarantee Send stays reachable at any width. */}
      {actionsAfter ? (
        <div className="flex shrink-0 items-center gap-0.5">
          <Separator />
          {actionsAfter}
        </div>
      ) : null}
    </div>
    {/* Sentinel for sticky-stuck detection. Placed AFTER the toolbar
        in DOM order so when the composer body extends past viewport
        bottom (and the toolbar sticks), this 1px div is offscreen
        below — the IntersectionObserver above flips `isStuck` to true,
        which morphs the toolbar from docked-strip to floating-island.
        Hidden from accessibility tree. */}
    {position === 'bottom' ? (
      <div ref={sentinelRef} className="h-px w-full" aria-hidden />
    ) : null}
    </>
  );
}

function ToolbarButton({
  active,
  title,
  onClick,
  children,
  ...rest
}: {
  active?: boolean;
  title: string;
  onClick: () => void;
  children: React.ReactNode;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'title'>) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className={cn(
        // `shrink-0` so the glyphs keep their 28px hit area inside the
        // scrolling cluster instead of compressing below it — ReplyComposer's
        // own ToolbarButton already had this and these two had drifted apart.
        'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-muted-foreground transition-colors',
        'hover:bg-accent hover:text-foreground',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active && 'bg-accent text-foreground',
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

function Separator() {
  return <span aria-hidden className="mx-0.5 h-4 w-px self-center bg-border/60" />;
}

// Walk up from `node` to `boundary` looking for an ancestor with the given
// uppercase tag name. Returns the matching element or null. Used for
// detecting whether the caret is inside a `<code>` or `<blockquote>` —
// neither has an execCommand state query.
function findAncestor(node: Node, boundary: HTMLElement, tagName: string): HTMLElement | null {
  let cur: Node | null = node;
  while (cur && cur !== boundary) {
    if (cur.nodeType === Node.ELEMENT_NODE && (cur as HTMLElement).tagName === tagName) {
      return cur as HTMLElement;
    }
    cur = cur.parentNode;
  }
  return null;
}

function hasAncestor(node: Node, boundary: HTMLElement, tagName: string): boolean {
  return findAncestor(node, boundary, tagName) !== null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
